import React, { useMemo, useState, useRef, useEffect } from "react";
import { getCountries } from "react-phone-number-input";
import en from "react-phone-number-input/locale/en.json";
import { Check, ChevronDown, Search } from "lucide-react";

/**
 * CountryField — searchable country dropdown matching the HuddleUp
 * design system. Uses the same country list source as PhoneField for
 * consistency.
 *
 * Props:
 *   value          ISO alpha-2 country code (e.g. "AE")
 *   onChange       ({ code, name }) => void
 *   testId         data-testid prefix
 *   invalid        bool
 */
export default function CountryField({
  value,
  onChange,
  testId = "country",
  invalid = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const countries = useMemo(() => {
    const list = getCountries().map((c) => ({
      code: c,
      name: en[c] || c,
    }));
    const priority = ["SA", "AE", "GB", "US", "CA", "IN", "PK", "QA", "BH", "KW", "SG"];
    const top = priority
      .map((code) => list.find((x) => x.code === code))
      .filter(Boolean);
    const rest = list
      .filter((x) => !priority.includes(x.code))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...top, ...rest];
  }, []);

  const filtered = useMemo(() => {
    if (!search) return countries;
    const q = search.toLowerCase();
    return countries.filter(
      (c) => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q),
    );
  }, [countries, search]);

  const selected = countries.find((c) => c.code === value);

  const select = (c) => {
    setOpen(false);
    setSearch("");
    onChange({ code: c.code, name: c.name });
  };

  return (
    <div className="relative" data-testid={`${testId}-wrapper`} ref={wrapperRef}>
      <button
        type="button"
        data-testid={`${testId}-trigger`}
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between pb-3 pt-1 text-left border-b transition-colors duration-500 ${
          invalid ? "border-red-500" : "border-charcoal/25 hover:border-gold"
        }`}
      >
        <span
          className={`font-display text-lg ${
            selected ? "text-charcoal" : "text-charcoal/30"
          }`}
        >
          {selected ? selected.name : "Select country"}
        </span>
        <ChevronDown size={16} className="text-charcoal/50" />
      </button>

      {open && (
        <div
          data-testid={`${testId}-list`}
          className="absolute z-30 mt-2 left-0 right-0 bg-white border border-charcoal/15 shadow-2xl max-h-[360px] flex flex-col"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-charcoal/10">
            <Search size={14} className="text-charcoal/50" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country"
              data-testid={`${testId}-search`}
              className="flex-1 bg-transparent outline-none text-sm text-charcoal placeholder-charcoal/40"
            />
          </div>
          <div className="overflow-y-auto flex-1">
            {filtered.length === 0 && (
              <div className="px-4 py-6 text-sm text-charcoal/50">No match.</div>
            )}
            {filtered.map((c) => (
              <button
                key={c.code}
                type="button"
                data-testid={`${testId}-option-${c.code}`}
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); select(c); }}
                className={`w-full flex items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-cream transition-colors ${
                  c.code === value ? "bg-cream" : ""
                }`}
              >
                <span className="flex items-center gap-3">
                  <span className="text-xs font-medium text-charcoal/50 w-8">
                    {c.code}
                  </span>
                  <span className="text-charcoal">{c.name}</span>
                </span>
                {c.code === value && <Check size={14} className="text-gold" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
